import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import {
  apiDataFetchingErrorsAtom,
  controlsSF,
  createErrorResult,
  errorsCrudContentAtomFamily,
  filterOnlyDirty,
  FutureBookingsConfirmOutcome,
  FutureBookingsImpactApi,
  getSiblingToValidationFields,
  IExtendableDto,
  IPassengerDto,
  Log,
  PASSENGER_STATION_FIELDS,
  SimpleResult,
} from "@ryde/shared";

import { useCrudEditContext } from "../../contexts";
import {
  buildPassengerStationsChange,
  resolveShouldUpdateFutureBookingsStations,
  useCrudSave,
  useFutureBookingsConfirm,
} from "../dynamic-forms";

/**
 * Dedicated passenger save flow (mirrors useShiftEdit).
 *
 * Validates the form FIRST, then — only when one of the passenger's stations was actually replaced —
 * asks the admin whether future "New"/"Draft" bookings still sitting on the OLD station should follow
 * (RYDE-7929). The passenger itself saves either way; only the booking propagation is gated.
 */
export const usePassengerEdit = <T extends IExtendableDto>() => {
  const {
    entity,
    data: { isEdit, save, dto: contextDto },
  } = useCrudEditContext<T>();
  const { saveCrudEntity } = useCrudSave<T>();
  const { confirmUpdateFutureBookingsStations } = useFutureBookingsConfirm();
  const { t } = useTranslation();
  const controls = useRecoilValue(controlsSF(entity));
  const {
    getValues,
    trigger,
    formState: { dirtyFields },
  } = useFormContext();
  const setCrudServerErrors = useSetRecoilState(
    errorsCrudContentAtomFamily(entity),
  );
  const setApiServerErrors = useSetRecoilState(apiDataFetchingErrorsAtom);

  const savePassenger = useEvent(async (dto?: T): Promise<SimpleResult<T>> => {
    setCrudServerErrors(undefined);
    setApiServerErrors(undefined);

    // Validate before prompting so the admin is never asked to confirm and then blocked by a form error.
    const isValidForm = await trigger();
    if (!isValidForm) {
      Log.warnAndToast(t("errors.formIsInvalidCheckValidationErrors"));
      return createErrorResult<T>("errors.formIsInvalidCheckValidationErrors");
    }

    const formData = getValues() as Partial<T> & Partial<IPassengerDto>;
    const passengerId = contextDto?.id ?? dto?.id;

    let outcome = FutureBookingsConfirmOutcome.NotApplicable;

    if (passengerId) {
      const passengerStationsChange = buildPassengerStationsChange(formData);

      outcome = await confirmUpdateFutureBookingsStations({
        isEdit: Boolean(isEdit),
        dirtyFields,
        triggerFields: PASSENGER_STATION_FIELDS,
        getImpactCount: () =>
          FutureBookingsImpactApi.forPassenger(
            passengerId,
            passengerStationsChange,
          ),
      });
    }

    // Never guess when the impact is unknown: station propagation defaults to ON server-side, so saving
    // without an explicit answer would cascade a mass change nobody approved. Abort and let them retry.
    // The API layer already toasted the real server error, so don't stack a generic one on top.
    if (outcome === FutureBookingsConfirmOutcome.PreviewFailed) {
      return createErrorResult<T>("errors.failedToCheckFutureBookingsImpact");
    }

    const shouldUpdateFutureBookingsStations =
      resolveShouldUpdateFutureBookingsStations(outcome);

    // No station was replaced, or we are creating/duplicating: reuse the generic save path,
    // which also handles duplicate-mode payloads.
    if (
      shouldUpdateFutureBookingsStations === undefined ||
      save === undefined
    ) {
      return saveCrudEntity(dto);
    }

    const siblingToValidationFields = getSiblingToValidationFields({
      controls,
      formFields: Object.keys(formData),
    });
    const formDataCleaned = filterOnlyDirty({
      formFields: formData,
      dirtyFields,
      siblingToValidationFields,
    });

    // Sent explicitly even when false: station propagation defaults to ON server-side, so declining
    // the popup has to be stated, not implied by omission.
    const passengerForApiSend: Partial<T> & {
      shouldUpdateFutureBookingsStations: boolean;
    } = {
      ...formDataCleaned,
      shouldUpdateFutureBookingsStations,
    };

    return save(passengerForApiSend);
  });

  return { savePassenger };
};
